// TDEE calculation and management service for Milestone 2

import dayjs from "dayjs";
import {
  getDocumentByUserId,
  addDocument,
  updateDocument,
} from "./firebase/firestore.service";
import type {
  BodyMetrics,
  TDEEConfig,
  DeficitPlan,
  DailyTDEE,
} from "../types/tdee";
import { getDailyNutrition, autoCreateTargetRanges } from "./nutrition.service";
import { getDailyBurn } from "./workout.service";

import { getEffectiveAgeYears, computeAgeFromDOB } from "../utils/age";

const BODY_METRICS_COLLECTION = "bodyMetrics";
const TDEE_CONFIG_COLLECTION = "tdeeConfig";
const DEFICIT_PLAN_COLLECTION = "deficitPlan";

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Determine which fields are missing for TDEE calculation.
 * Accepts either `age` or `dateOfBirth` to satisfy the age requirement.
 * Single source of truth — used by saveBodyMetrics gate AND calculateDailyTDEE lazy path.
 */
export function getMissingTdeeFields(metrics: Partial<BodyMetrics> | null): string[] {
  if (!metrics) return ['currentWeight', 'height', 'age/dateOfBirth', 'sex'];
  const missing: string[] = [];
  if (!metrics.currentWeight) missing.push('currentWeight');
  if (!metrics.height) missing.push('height');
  if (!metrics.age && !metrics.dateOfBirth) missing.push('age/dateOfBirth');
  if (!metrics.sex) missing.push('sex');
  return missing;
}

/**
 * Resolve effective age from body metrics.
 * Priority: age + ageCapturedAt (roll-forward) → age (raw) → dateOfBirth (computed).
 * Returns null if neither age nor DOB are available.
 */
function resolveEffectiveAge(metrics: BodyMetrics): number | null {
  // If age exists, use the roll-forward mechanism
  if (metrics.age) {
    return getEffectiveAgeYears(metrics.age, metrics.ageCapturedAt) ?? metrics.age;
  }
  // Fallback: compute from DOB
  return computeAgeFromDOB(metrics.dateOfBirth);
}

/**
 * Derive and persist `age` + `ageCapturedAt` from `dateOfBirth` if age is missing.
 * Called during body metrics save to ensure the TDEE gate can pass.
 * Returns the updated metrics object (in-memory — Firestore write is the caller's job).
 */
async function backfillAgeFromDOB(
  metrics: BodyMetrics,
  docPath: string,
): Promise<BodyMetrics> {
  if (metrics.age || !metrics.dateOfBirth) return metrics;

  const derivedAge = computeAgeFromDOB(metrics.dateOfBirth);
  if (derivedAge == null) return metrics;

  const now = Date.now();
  const ageFields = { age: derivedAge, ageCapturedAt: now, updatedAt: now };

  try {
    await updateDocument(docPath, ageFields);
    console.log('[TDEE] Backfilled age from DOB:', {
      dateOfBirth: metrics.dateOfBirth,
      derivedAge,
      docPath,
    });
  } catch (err) {
    console.warn('[TDEE] Failed to backfill age (non-critical):', err);
  }

  return { ...metrics, ...ageFields };
}

/**
 * Calculate Baseline TDEE using Mifflin-St Jeor equation
 *
 * Baseline TDEE = BMR × fixed activity factor (1.2 for sedentary)
 *
 * This represents estimated calories burned per day from normal living,
 * before adding any logged workouts or steps.
 *
 * Baseline TDEE stays constant unless:
 * - User edits their targets, OR
 * - User updates Body & Goals and explicitly accepts new suggested targets
 *
 * DO NOT change TDEE automatically day-to-day in the background.
 *
 * @returns Baseline TDEE in kcal (sedentary)
 */
export function calculateBaseTDEE(
  weight: number, // kg
  height: number, // cm
  age: number,
  sex: "male" | "female" | "other",
): number {
  // Mifflin-St Jeor equation for BMR (Basal Metabolic Rate)
  // Men: BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age(years) + 5
  // Women: BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age(years) - 161

  let bmr: number;

  if (sex === "male") {
    bmr = 10 * weight + 6.25 * height - 5 * age + 5;
  } else if (sex === "female") {
    bmr = 10 * weight + 6.25 * height - 5 * age - 161;
  } else {
    // For 'other', use average of male and female
    const maleBmr = 10 * weight + 6.25 * height - 5 * age + 5;
    const femaleBmr = 10 * weight + 6.25 * height - 5 * age - 161;
    bmr = (maleBmr + femaleBmr) / 2;
  }

  // Fixed activity factor for sedentary lifestyle (1.2)
  // Baseline TDEE = BMR × 1.2
  return Math.round(bmr * 1.2);
}

/**
 * Get or create body metrics for a user
 */
export async function getBodyMetrics(
  userId: string,
): Promise<BodyMetrics | null> {
  return getDocumentByUserId<BodyMetrics>(BODY_METRICS_COLLECTION, userId);
}

/**
 * Save or update body metrics
 * Automatically triggers TDEE calculation if all required fields are present
 */
export async function saveBodyMetrics(
  userId: string,
  metrics: Omit<BodyMetrics, "id" | "userId" | "createdAt" | "updatedAt">,
): Promise<BodyMetrics> {
  console.log('[saveBodyMetrics] START uid:', userId);

  let existing: BodyMetrics | null;
  try {
    existing = await getBodyMetrics(userId);
    console.log('[saveBodyMetrics] getBodyMetrics:', existing ? `found id=${existing.id}` : 'null — will create');
  } catch (e) {
    console.error('[saveBodyMetrics] getBodyMetrics FAILED:', e);
    throw e;
  }

  const now = Date.now();

  // Remove undefined values (Firestore doesn't accept undefined)
  const cleanedMetrics = Object.fromEntries(
    Object.entries(metrics).filter(([, value]) => value !== undefined),
  ) as Omit<BodyMetrics, "id" | "userId" | "createdAt" | "updatedAt">;

  let savedMetrics: BodyMetrics;

  if (existing) {
    const updatePayload = { ...cleanedMetrics, updatedAt: now };
    console.log('[saveBodyMetrics] UPDATE path — doc:', `${BODY_METRICS_COLLECTION}/${existing.id}`, 'payload keys:', Object.keys(updatePayload), 'userId in payload:', 'userId' in updatePayload);
    try {
      await updateDocument(`${BODY_METRICS_COLLECTION}/${existing.id}`, updatePayload);
      console.log('[saveBodyMetrics] updateDocument: OK');
    } catch (e) {
      console.error('[saveBodyMetrics] updateDocument FAILED:', e);
      throw e;
    }
    savedMetrics = {
      ...existing,
      ...cleanedMetrics,
      updatedAt: now,
    };
  } else {
    const createPayload = { ...cleanedMetrics, userId, createdAt: now, updatedAt: now };
    console.log('[saveBodyMetrics] CREATE path — payload keys:', Object.keys(createPayload), 'userId:', createPayload.userId);
    try {
      savedMetrics = await addDocument<Omit<BodyMetrics, "id">>(
        BODY_METRICS_COLLECTION,
        createPayload,
      );
      console.log('[saveBodyMetrics] addDocument: OK id:', savedMetrics.id);
    } catch (e) {
      console.error('[saveBodyMetrics] addDocument FAILED:', e);
      throw e;
    }
  }

  // CRITICAL: If DOB exists but age doesn't, derive and persist age.
  // This fixes the onboarding gap where DOB is saved but age is not.
  const docPath = `${BODY_METRICS_COLLECTION}/${savedMetrics.id}`;
  savedMetrics = await backfillAgeFromDOB(savedMetrics, docPath);

  // CRITICAL: Automatically calculate TDEE and create targets if all required fields are present
  // Uses getMissingTdeeFields (shared helper) — accepts either age or dateOfBirth.
  const missing = getMissingTdeeFields(savedMetrics);
  if (missing.length === 0) {
    try {
      await calculateAndSaveBaseTDEE(userId);
      console.log('[TDEE] Auto-calculated TDEE after body metrics save:', { userId });
    } catch (error) {
      // Don't fail the save if TDEE calculation fails, but log it
      console.error(
        "[TDEE] Failed to auto-calculate TDEE after saving body metrics:",
        error,
        { userId, missing: getMissingTdeeFields(savedMetrics) },
      );
    }
  } else {
    console.log('[TDEE] Skipping auto-TDEE: missing fields:', missing);
  }

  return savedMetrics;
}

/**
 * Get or create TDEE config for a user
 */
export async function getTDEEConfig(
  userId: string,
): Promise<TDEEConfig | null> {
  return getDocumentByUserId<TDEEConfig>(TDEE_CONFIG_COLLECTION, userId);
}

/**
 * Calculate and save base TDEE from body metrics
 */
export async function calculateAndSaveBaseTDEE(
  userId: string,
): Promise<TDEEConfig> {
  const metrics = await getBodyMetrics(userId);

  const missing = getMissingTdeeFields(metrics);
  if (missing.length > 0) {
    throw new Error(
      `Body metrics incomplete. Missing: ${missing.join(', ')}`,
    );
  }

  // Resolve effective age: age + roll-forward, or compute from DOB
  const effectiveAge = resolveEffectiveAge(metrics!);
  if (effectiveAge == null) {
    throw new Error("Cannot determine age from body metrics.");
  }

  const baseTdee = calculateBaseTDEE(
    metrics!.currentWeight,
    metrics!.height!,
    effectiveAge,
    metrics!.sex!,
  );

  const existing = await getTDEEConfig(userId);
  const now = Date.now();

  if (existing) {
    const effectiveBaseTdee = existing.manualOverride || baseTdee;
    await updateDocument(`${TDEE_CONFIG_COLLECTION}/${existing.id}`, {
      baseTdee,
      effectiveBaseTdee,
      calculatedAt: now,
      updatedAt: now,
    });

    // CRITICAL: Auto-create targets even if TDEE already exists (if targets don't exist)
    // This ensures targets are created after onboarding even if TDEE was created separately
    try {
      await autoCreateTargetRanges(
        userId,
        metrics!.currentWeight,
        baseTdee,
        metrics!.goalWeight,
        metrics!.sex,
      );
    } catch (error) {
      // Don't fail TDEE update if target creation fails
      console.error("Failed to auto-create nutrition targets:", error);
    }

    return {
      ...existing,
      baseTdee,
      effectiveBaseTdee,
      calculatedAt: now,
      updatedAt: now,
    };
  } else {
    const newConfig = await addDocument<Omit<TDEEConfig, "id">>(
      TDEE_CONFIG_COLLECTION,
      {
        userId,
        baseTdee,
        effectiveBaseTdee: baseTdee,
        exerciseCalories: {},
        calculatedAt: now,
        updatedAt: now,
      },
    );

    // Auto-create nutrition targets when TDEE is first calculated
    // This ensures targets are available immediately after onboarding
    try {
      await autoCreateTargetRanges(
        userId,
        metrics!.currentWeight,
        baseTdee,
        metrics!.goalWeight,
        metrics!.sex,
      );
    } catch (error) {
      // Don't fail TDEE creation if target creation fails
      console.error("Failed to auto-create nutrition targets:", error);
    }

    return newConfig;
  }
}

/**
 * Set manual TDEE override
 */
export async function setManualTDEEOverride(
  userId: string,
  override: number, // kcal
): Promise<TDEEConfig> {
  const existing = await getTDEEConfig(userId);
  const now = Date.now();

  if (!existing) {
    throw new Error("TDEE config not found. Please calculate base TDEE first.");
  }

  await updateDocument(`${TDEE_CONFIG_COLLECTION}/${existing.id}`, {
    manualOverride: override,
    effectiveBaseTdee: override,
    updatedAt: now,
  });

  return {
    ...existing,
    manualOverride: override,
    effectiveBaseTdee: override,
    updatedAt: now,
  };
}

/**
 * Clear manual TDEE override (use calculated base)
 */
export async function clearManualTDEEOverride(
  userId: string,
): Promise<TDEEConfig> {
  const existing = await getTDEEConfig(userId);

  if (!existing) {
    throw new Error("TDEE config not found.");
  }

  const now = Date.now();

  await updateDocument(`${TDEE_CONFIG_COLLECTION}/${existing.id}`, {
    manualOverride: null,
    effectiveBaseTdee: existing.baseTdee,
    updatedAt: now,
  });

  return {
    ...existing,
    manualOverride: undefined,
    effectiveBaseTdee: existing.baseTdee,
    updatedAt: now,
  };
}

/**
 * Set exercise calories for a specific date
 */
export async function setExerciseCalories(
  userId: string,
  date: string, // YYYY-MM-DD
  calories: number, // kcal
): Promise<TDEEConfig> {
  const existing = await getTDEEConfig(userId);
  const now = Date.now();

  if (!existing) {
    throw new Error("TDEE config not found. Please calculate base TDEE first.");
  }

  const exerciseCalories = {
    ...existing.exerciseCalories,
    [date]: calories,
  };

  await updateDocument(`${TDEE_CONFIG_COLLECTION}/${existing.id}`, {
    exerciseCalories,
    updatedAt: now,
  });

  return {
    ...existing,
    exerciseCalories,
    updatedAt: now,
  };
}

/**
 * Calculate daily TDEE for a specific date
 * Includes EPOC (Excess Post-Exercise Oxygen Consumption) from previous day
 * EPOC = 10% of previous day's total exercise calories
 */
export async function calculateDailyTDEE(
  userId: string,
  date: string, // YYYY-MM-DD
): Promise<DailyTDEE> {
  const [tdeeConfig, dailyNutrition, dailyBurn] = await Promise.all([
    getTDEEConfig(userId),
    getDailyNutrition(userId, date),
    getDailyBurn(userId, date).catch(() => null), // Get workout burn, but don't fail if no workouts
  ]);

  // If no TDEE config, attempt lazy self-heal: compute from body metrics and persist.
  // This fixes users who completed onboarding but never got tdeeConfig created
  // (e.g., because onboarding saved dateOfBirth but not age).
  if (!tdeeConfig) {
    const metrics = await getBodyMetrics(userId);
    const missing = getMissingTdeeFields(metrics);

    if (missing.length === 0 && metrics) {
      // All fields present — compute and save tdeeConfig (one-time self-heal)
      try {
        // Backfill age from DOB if needed (idempotent)
        const docPath = `${BODY_METRICS_COLLECTION}/${metrics.id}`;
        await backfillAgeFromDOB(metrics, docPath);

        const newConfig = await calculateAndSaveBaseTDEE(userId);
        console.log('[TDEE] Lazy self-heal: created tdeeConfig from body metrics', {
          userId, baseTdee: newConfig.baseTdee,
        });

        // Re-run with newly created config (recursive, but only once — config now exists)
        return calculateDailyTDEE(userId, date);
      } catch (err) {
        console.error('[TDEE] Lazy self-heal failed:', err);
        // Fall through to unconfigured return
      }
    }

    // Unconfigured: return zeros with explicit isConfigured: false
    // Deficit is 0 (not computed) to prevent misleading UI values
    console.warn('[TDEE] No tdeeConfig and cannot self-heal. Missing:', missing);
    return {
      date,
      userId,
      baseTdee: 0,
      exerciseCalories: 0,
      epoc: 0,
      totalTdee: 0,
      caloriesEaten: dailyNutrition.totalCalories,
      deficit: 0,
      isConfigured: false,
      missingFields: missing,
    };
  }

  // REQUIRED FIX: Match exact specification from STEPS CALCULATION SIMPLE BUT COMPREHENSIVE EXPLANATION
  //
  // A) Resting = baseTdee (restingCalories)
  // Baseline TDEE = BMR (Mifflin-St Jeor) × fixed activity factor (1.2 for sedentary)
  // This represents estimated calories burned per day from normal living,
  // before adding any logged workouts or steps.
  const restingCalories = tdeeConfig.effectiveBaseTdee;

  // CRITICAL: If NO workouts logged → exerciseCalories = 0
  // ❌ NO baked-in default exercise calories
  // ❌ NO ghost workouts affecting burn
  // Check if there are actual workout entries (not just calories)
  const hasWorkoutEntries =
    dailyBurn && dailyBurn.entries && dailyBurn.entries.length > 0;

  // D) EXERCISE CALORIES (ALL WORKOUTS):
  // exerciseCalories = sum(activityCalories for ALL workouts)
  // IMPORTANT: Run calories ALWAYS count. Gym calories ALWAYS count. Other calories ALWAYS count.
  // Step calories NEVER go inside exerciseCalories.
  // So: exerciseCalories = runBurn + weightliftingBurn + manualBurn (NO step calories)
  //
  // CRITICAL: When hasWorkoutEntries = false, exerciseCalories MUST be 0
  // This prevents ghost calories and baked-in defaults
  const exerciseCalories = hasWorkoutEntries
    ? (dailyBurn?.runsWalksBurn ?? 0) +
      (dailyBurn?.weightliftingBurn ?? 0) +
      (dailyBurn?.manualBurn ?? 0)
    : 0;

  // C) STEP CALORIES:
  // stepCalories = stepsForStepCalories × stepRate
  const stepCalories = dailyBurn?.stepsBurn ?? 0;

  // Calculate EPOC from previous day
  // EPOC = 10% of previous day's total exercise calories (all exercise types)
  // If no exercise the previous day, EPOC = 0
  let epocCalories = 0;
  const currentDate = dayjs(date);
  const previousDate = currentDate.subtract(1, "day").format("YYYY-MM-DD");

  // Get previous day's exercise calories (exercise only, not step calories)
  const previousDayBurn = await getDailyBurn(userId, previousDate).catch(
    () => null,
  );
  const previousDayExerciseCalories = previousDayBurn
    ? (previousDayBurn.runsWalksBurn ?? 0) +
      (previousDayBurn.weightliftingBurn ?? 0) +
      (previousDayBurn.manualBurn ?? 0)
    : 0;

  // EPOC = 10% of previous day's total exercise calories
  if (previousDayExerciseCalories > 0) {
    epocCalories = Math.round(previousDayExerciseCalories * 0.1);
  }

  // E) DAILY BURN:
  // totalDailyBurn = restingCalories + exerciseCalories + stepCalories + epocCalories
  // NOTHING ELSE GOES IN.
  const totalDailyBurn =
    restingCalories + exerciseCalories + stepCalories + epocCalories;

  // F) DAILY DEFICIT:
  // dailyDeficit = totalDailyBurn − caloriesConsumed
  const caloriesEaten = dailyNutrition.totalCalories;
  const deficit = totalDailyBurn - caloriesEaten;

  return {
    date,
    userId,
    baseTdee: restingCalories,
    exerciseCalories,
    epoc: epocCalories,
    totalTdee: totalDailyBurn, // totalTdee = totalDailyBurn for consistency
    caloriesEaten,
    deficit,
    isConfigured: true,
  };
}

/**
 * Calculate deficit plan from weight goals
 */
export function calculateDeficitPlan(
  currentWeight: number, // kg
  goalWeight: number, // kg
  timeframeWeeks: number,
): {
  totalDeficitNeeded: number; // kcal
  dailyDeficitSuggested: { min: number; max: number };
  weeklyDeficitSuggested: { min: number; max: number };
} {
  // 1 kg of fat ≈ 7,700 kcal
  const weightToLose = currentWeight - goalWeight;
  const totalDeficitNeeded = weightToLose * 7700;

  // Calculate daily deficit
  const totalDays = timeframeWeeks * 7;
  const dailyDeficitAverage = totalDeficitNeeded / totalDays;

  // Cap to safe ranges: 250-750 kcal/day deficit
  const dailyDeficitMin = Math.max(
    250,
    Math.min(500, dailyDeficitAverage * 0.8),
  );
  const dailyDeficitMax = Math.min(
    750,
    Math.max(350, dailyDeficitAverage * 1.2),
  );

  // Weekly deficit
  const weeklyDeficitMin = dailyDeficitMin * 7;
  const weeklyDeficitMax = dailyDeficitMax * 7;

  return {
    totalDeficitNeeded,
    dailyDeficitSuggested: {
      min: Math.round(dailyDeficitMin),
      max: Math.round(dailyDeficitMax),
    },
    weeklyDeficitSuggested: {
      min: Math.round(weeklyDeficitMin),
      max: Math.round(weeklyDeficitMax),
    },
  };
}

/**
 * Get or create deficit plan for a user
 */
export async function getDeficitPlan(
  userId: string,
): Promise<DeficitPlan | null> {
  return getDocumentByUserId<DeficitPlan>(DEFICIT_PLAN_COLLECTION, userId);
}

/**
 * Create or update deficit plan
 */
export async function saveDeficitPlan(
  userId: string,
  plan: Omit<
    DeficitPlan,
    | "id"
    | "userId"
    | "totalDeficitNeeded"
    | "dailyDeficitSuggested"
    | "weeklyDeficitSuggested"
    | "dailyDeficitActive"
    | "weeklyDeficitActive"
    | "createdAt"
    | "updatedAt"
  >,
): Promise<DeficitPlan> {
  const calculated = calculateDeficitPlan(
    plan.currentWeight,
    plan.goalWeight,
    plan.timeframeWeeks,
  );

  const dailyDeficitActive =
    plan.dailyDeficitCustom || calculated.dailyDeficitSuggested;
  const weeklyDeficitActive =
    plan.weeklyDeficitCustom || calculated.weeklyDeficitSuggested;

  const existing = await getDeficitPlan(userId);
  const now = Date.now();

  // Remove undefined values (Firestore doesn't accept undefined)
  const cleanedPlan = Object.fromEntries(
    Object.entries(plan).filter(([, value]) => value !== undefined),
  ) as typeof plan;

  if (existing) {
    await updateDocument(`${DEFICIT_PLAN_COLLECTION}/${existing.id}`, {
      ...cleanedPlan,
      ...calculated,
      dailyDeficitActive,
      weeklyDeficitActive,
      updatedAt: now,
    });
    return {
      ...existing,
      ...cleanedPlan,
      ...calculated,
      dailyDeficitActive,
      weeklyDeficitActive,
      updatedAt: now,
    };
  } else {
    // Remove undefined values (Firestore doesn't accept undefined)
    const cleanedPlan = Object.fromEntries(
      Object.entries(plan).filter(([, value]) => value !== undefined),
    ) as typeof plan;

    return addDocument<Omit<DeficitPlan, "id">>(DEFICIT_PLAN_COLLECTION, {
      ...cleanedPlan,
      ...calculated,
      dailyDeficitActive,
      weeklyDeficitActive,
      userId,
      createdAt: now,
      updatedAt: now,
    });
  }
}

// ─── getDailyTDEECached ───────────────────────────────────────────────────────
//
// Thin cached wrapper around calculateDailyTDEE.
// Intended for weekly evaluation passes (celebration service, messaging engine)
// where the same userId+date window is queried multiple times in one session.
//
// Cache policy:
//   - Key: `${userId}_${date}`
//   - TTL: 60 seconds
//   - Failures are NOT cached — every failed call retries fresh
//   - Null results are NOT cached
//
// calculateDailyTDEE() is left pure and unmodified.

interface CacheEntry {
  value: DailyTDEE;
  expiresAt: number;
}

const dailyTDEECache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60 * 1000;

export async function getDailyTDEECached(
  userId: string,
  date: string,
): Promise<DailyTDEE> {
  const key = `${userId}_${date}`;
  const now = Date.now();

  const cached = dailyTDEECache.get(key);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }

  const result = await calculateDailyTDEE(userId, date);

  // Only cache successful non-null results
  // Guard against runaway memory growth (unlikely in practice but safe)
  if (result != null) {
    if (dailyTDEECache.size > 100) dailyTDEECache.clear();
    dailyTDEECache.set(key, { value: result, expiresAt: now + CACHE_TTL_MS });
  }

  return result;
}
